import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Pause, Play, Square } from 'lucide-react';
import {
  type TimeStatus,
  type Workday,
  closeWorkday,
  getStatus,
  listHistory,
  openWorkday,
  pauseWorkday,
  resumeWorkday,
} from '@/api/timeman';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

function fmtDateTime(iso: string | null, locale: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(locale);
}

function durationLabel(openedAt: string, closedAt: string | null, pauseMin: number): string {
  const start = new Date(openedAt).getTime();
  const end = closedAt ? new Date(closedAt).getTime() : Date.now();
  const mins = Math.max(0, Math.round((end - start) / 60000) - pauseMin);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
}

export function TimeClockPage() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<TimeStatus | null>(null);
  const [history, setHistory] = useState<Workday[]>([]);
  const [location, setLocation] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    getStatus().then(setStatus).catch(() => undefined);
    listHistory().then(setHistory).catch(() => undefined);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      refresh();
    } finally {
      setBusy(false);
    }
  }

  const state = status?.status ?? 'closed';

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">
          {t('timeman.title')}
        </h1>
        <p className="text-sm text-[var(--color-lop-text-secondary)]">{t('timeman.subtitle')}</p>
      </header>

      {/* Status card */}
      <div className="rounded-lg border bg-card p-6" data-testid="timeman-status">
        <div className="flex items-center gap-3">
          <span
            className={`flex h-12 w-12 items-center justify-center rounded-full ${
              state === 'open'
                ? 'bg-emerald-900/40 text-emerald-300'
                : state === 'paused'
                  ? 'bg-amber-900/40 text-amber-300'
                  : 'bg-[var(--color-lop-bg-card-hi)] text-[var(--color-lop-text-muted)]'
            }`}
          >
            <Clock className="h-6 w-6" />
          </span>
          <div>
            <p className="text-lg font-semibold text-[var(--color-lop-text-primary)]">
              {t(`timeman.state.${state === 'anonymous' ? 'closed' : state}`)}
            </p>
            {status?.openedAt && (
              <p className="text-sm text-[var(--color-lop-text-secondary)]">
                {t('timeman.openedAt', { at: fmtDateTime(status.openedAt, i18n.language) })}
                {typeof status.totalPauseMinutes === 'number' && status.totalPauseMinutes > 0 && (
                  <> · {t('timeman.pausedMinutes', { min: status.totalPauseMinutes })}</>
                )}
              </p>
            )}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-end gap-3">
          {state === 'closed' && (
            <>
              <div className="flex-1">
                <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
                  {t('timeman.location')}
                </label>
                <Input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder={t('timeman.locationPlaceholder')}
                />
              </div>
              <Button onClick={() => act(() => openWorkday(location || undefined))} disabled={busy} data-testid="timeman-open">
                <Play className="mr-1 h-4 w-4" />
                {t('timeman.clockIn')}
              </Button>
            </>
          )}
          {state === 'open' && (
            <>
              <Button variant="outline" onClick={() => act(pauseWorkday)} disabled={busy} data-testid="timeman-pause">
                <Pause className="mr-1 h-4 w-4" />
                {t('timeman.pause')}
              </Button>
              <Button variant="destructive" onClick={() => act(() => closeWorkday())} disabled={busy} data-testid="timeman-close">
                <Square className="mr-1 h-4 w-4" />
                {t('timeman.clockOut')}
              </Button>
            </>
          )}
          {state === 'paused' && (
            <>
              <Button onClick={() => act(resumeWorkday)} disabled={busy} data-testid="timeman-resume">
                <Play className="mr-1 h-4 w-4" />
                {t('timeman.resume')}
              </Button>
              <Button variant="destructive" onClick={() => act(() => closeWorkday())} disabled={busy}>
                <Square className="mr-1 h-4 w-4" />
                {t('timeman.clockOut')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* History */}
      <div>
        <h2 className="mb-2 text-sm font-medium text-[var(--color-lop-text-primary)]">
          {t('timeman.history')}
        </h2>
        <div className="overflow-hidden rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-lop-bg-card-hi)] text-left text-xs text-[var(--color-lop-text-secondary)]">
              <tr>
                <th className="px-3 py-2 font-medium">{t('timeman.col.opened')}</th>
                <th className="px-3 py-2 font-medium">{t('timeman.col.closed')}</th>
                <th className="px-3 py-2 font-medium">{t('timeman.col.worked')}</th>
                <th className="px-3 py-2 font-medium">{t('timeman.col.location')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-lop-border)]">
              {history.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                    {t('timeman.noHistory')}
                  </td>
                </tr>
              )}
              {history.map((w) => (
                <tr key={w.id} className="text-[var(--color-lop-text-primary)]">
                  <td className="px-3 py-2">{fmtDateTime(w.openedAt, i18n.language)}</td>
                  <td className="px-3 py-2">{fmtDateTime(w.closedAt, i18n.language)}</td>
                  <td className="px-3 py-2">{durationLabel(w.openedAt, w.closedAt, w.totalPauseMinutes)}</td>
                  <td className="px-3 py-2 text-[var(--color-lop-text-secondary)]">{w.location ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
